import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import { QrCode } from 'lucide-react';
import TcImage from '../components/TcImage';
import imgScanToBegin from '../assets/images/tutorial-scan-to-begin.png';
import { useSimpleImagePreloader } from '../hooks/useImagePreloader';
import LoadingScreen from '../components/shared/LoadingScreen';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { useCameraCleanup } from '../hooks/useCameraCleanup';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

export default function ReadyToExplore() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // The camera cleanup automatically stops the camera when the component mounts
    const { isForceStoppingCamera } = useCameraCleanup();
    
    // Use the custom hook for scroll detection
    const isAtBottom = useScrollToBottom({
        containerSelector: '.main-content-wrapper'
    });

    // Preload all images when component mounts
    const allImages = [
        imgScanToBegin
    ];

    const { isLoading } = useSimpleImagePreloader(allImages);
    
    const handleGotIt = () => {
        navigate('/leave-your-mark');
    };


    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading..." />;
    }
    
    return (
        <div className={`
            main-content-wrapper !pb-24 bg-image-galaxy 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-6 mb-4">
                <h1 className="main-content-title">
                    {t('readyToExplore.title')}
                </h1>
            </TcPaper>
            
            <TcPaper classNameStyle="general-content">
                <p className="mb-4"
                    dangerouslySetInnerHTML={{ __html: t('readyToExplore.description') }}
                />
                
                {imgScanToBegin ? (
                    <TcImage classNameStyle="mb-6">
                        <div className={`
                            relative 
                            w-2/3 2xs:w-full xs:w-5/6 sm:w-2/3 md:w-2/3 lg:w-1/2 
                        `}>
                            <img src={imgScanToBegin} alt="Scan to begin" className="relative w-full h-full object-cover" />
                        </div>
                    </TcImage>
                ) : (
                    <div className="rounded-lg px-6 py-2 mb-6">
                        <div className="flex justify-center mb-4">
                            <QrCode size={120} className="text-white" />
                        </div>
                    </div>
                )}
                
                <div className={`button-wrapper stick-to-bottom p-4 !bottom-[4vw] !max-w-[92vw] has-transition ${
                    isAtBottom ? 'clear-transparent' : 'glossy-translucent-with-border'
                }`}>
                    <TcButton 
                        handleClick={handleGotIt}
                        primary
                        disabled={isForceStoppingCamera}
                    >
                        {isForceStoppingCamera ? t('common.gettingReady') : t('readyToExplore.gotItButton')}
                    </TcButton>
                </div>
                
            </TcPaper>
        </div>
    );
} 